/**
 * device/{userId}/{deviceId}/status 接收侧解析：先读 type，再分支处理。
 * 输出统一信封 { type, deviceId, timestamp, data } 或兼容旧格式的 payload，供下游 AudioManager 消费。
 */

export function normalizeDeviceStatusMessageType(type: unknown): string {
  if (type == null) return '';
  return String(type).trim().toUpperCase();
}

export interface DeviceStatusMqttEnvelope {
  type: string;
  deviceId: string;
  timestamp: number;
  data: Record<string, unknown>;
}

function resolveTimestamp(
  raw: Record<string, unknown>,
  data?: Record<string, unknown>,
): number {
  for (const source of [raw, data ?? {}]) {
    const t = source.timestamp;
    if (typeof t === 'number' && Number.isFinite(t)) return t;
  }
  return Date.now();
}

function asDataRecord(value: unknown): Record<string, unknown> {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return {};
}

function buildTypedEnvelope(
  type: string,
  raw: Record<string, unknown>,
  deviceId: string,
): DeviceStatusMqttEnvelope {
  const data = asDataRecord(raw.data);
  return {
    type,
    deviceId: String(raw.deviceId ?? data.deviceId ?? deviceId),
    timestamp: resolveTimestamp(raw, data),
    data: Object.keys(data).length > 0 ? data : asDataRecord(raw),
  };
}

function parse0x0bEnvelope(
  raw: Record<string, unknown>,
  deviceId: string,
): DeviceStatusMqttEnvelope {
  const data = asDataRecord(raw.data);
  return {
    type: '0X0B',
    deviceId: String(raw.deviceId ?? data.deviceId ?? deviceId),
    timestamp: resolveTimestamp(raw, data),
    data,
  };
}

function parseLegacyPayload(
  raw: Record<string, unknown>,
  deviceId: string,
  topicDeviceId: string,
): { deviceId: string; payload: unknown } {
  if (typeof raw.status === 'string' && raw.type == null && raw.data == null) {
    return { deviceId: topicDeviceId, payload: raw.status };
  }

  const data = raw.data;
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    return { deviceId, payload: raw };
  }

  return { deviceId, payload: raw };
}

/**
 * 解析 device status MQTT JSON，按 type 路由后返回 deviceId 与下游 payload。
 */
export function parseDeviceStatusMqttMessage(
  raw: unknown,
  topicDeviceId: string,
): { deviceId: string; payload: unknown } {
  if (!raw || typeof raw !== 'object') {
    return { deviceId: topicDeviceId, payload: raw };
  }

  const obj = raw as Record<string, unknown>;
  const msgType = normalizeDeviceStatusMessageType(obj.type);
  const deviceId = String(obj.deviceId ?? topicDeviceId).trim() || topicDeviceId;

  switch (msgType) {
    case '0X0B':
      return { deviceId, payload: parse0x0bEnvelope(obj, deviceId) };
    case '0X2E':
    case '0X2D':
      return { deviceId, payload: buildTypedEnvelope(msgType, obj, deviceId) };
    default:
      return parseLegacyPayload(obj, deviceId, topicDeviceId);
  }
}
